import Grid from '@componentPrimitives/Grid';
import { createField, findNestedField } from '@formie/parse/formieParser';
import { useFormField } from '@formie/useForm';
import { FormControl, FormHelperText, FormLabel } from '@primitives';
import { gql } from 'graphql-request';
import { DefaultSelectField } from './DefaultSelectField';
import { DefaultTextField } from './DefaultTextField';
import { FieldErrors } from './FieldErrors';

gql`
  fragment fieldAddress on Field_Address {
    ...fieldBase
    fields {
      __typename
      handle
      label
      enabled
      required
      placeholder
      defaultValue
      prePopulate
      errorMessage
      ... on Field_AddressCountry {
        options {
          ...fieldOption
        }
      }
    }
  }
`;

export const Field_Address = createField<'Field_Address'>(({ field, ..._props }) => {
  const { fields, handle, label, instructions, labelPosition } = field;

  const address1 = findNestedField(fields, 'Field_Address1');
  const address2 = findNestedField(fields, 'Field_Address2');
  const address3 = findNestedField(fields, 'Field_Address3');
  const city = findNestedField(fields, 'Field_AddressCity');
  const state = findNestedField(fields, 'Field_AddressState');
  const zip = findNestedField(fields, 'Field_AddressZip');
  const country = findNestedField(fields, 'Field_AddressCountry');

  const { value, setValue, error, errors } = useFormField(handle, 'complex', {
    ...(address1?.enabled && { address1: address1.defaultValue ?? '' }),
    ...(address2?.enabled && { address2: address2.defaultValue ?? '' }),
    ...(address3?.enabled && { address3: address3.defaultValue ?? '' }),
    ...(city?.enabled && { city: city.defaultValue ?? '' }),
    ...(country?.enabled && { country: country.defaultValue ?? '' }),
    ...(state?.enabled && { state: state.defaultValue ?? '' }),
    ...(zip?.enabled && { zip: zip.defaultValue ?? '' }),
  });

  const handleChange =
    (fieldName: string) => (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setValue({ ...value, [fieldName]: ev.target.value });
    };

  return (
    <FormControl error={error} fullWidth>
      {/* <Stack spacing={3} mb={4}> */}
      <FormLabel component="legend" sx={{ mb: 2 }} hidden={labelPosition === 'hidden'}>
        {label}
      </FormLabel>

      {instructions && <FormHelperText>{instructions}</FormHelperText>}

      <Grid rowGutter>
        {address1?.enabled && (
          <Grid.Col xs={12}>
            <DefaultTextField
              {...{
                name: 'address1',
                onChange: handleChange('address1'),
                value: value['address1'] ?? '',
                helperText: address1.errorMessage,
                label: address1.label,
                placeholder: address1.placeholder,
                required: address1.required,
              }}
            />
          </Grid.Col>
        )}
        {address2?.enabled && (
          <Grid.Col xs={12}>
            <DefaultTextField
              {...{
                name: 'address2',
                onChange: handleChange('address2'),
                value: value['address2'] ?? '',
                helperText: address2.errorMessage,
                label: address2.label,
                placeholder: address2.placeholder,
                required: address2.required,
              }}
            />
          </Grid.Col>
        )}
        {address3?.enabled && (
          <Grid.Col xs={12}>
            <DefaultTextField
              {...{
                name: 'address3',
                onChange: handleChange('address3'),
                value: value['address3'] ?? '',
                helperText: address3.errorMessage,
                label: address3.label,
                placeholder: address3.placeholder,
                required: address3.required,
              }}
            />
          </Grid.Col>
        )}
        <Grid.Col xs={12} sm={6}>
          {city?.enabled && (
            <DefaultTextField
              {...{
                name: 'city',
                onChange: handleChange('city'),
                value: value['city'] ?? '',
                helperText: city.errorMessage,
                label: city.label,
                placeholder: city.placeholder,
                required: city.required,
              }}
            />
          )}
        </Grid.Col>
        <Grid.Col xs={12} sm={6}>
          {state?.enabled && (
            <DefaultTextField
              {...{
                name: 'state',
                onChange: handleChange('state'),
                value: value['state'] ?? '',
                helperText: state.errorMessage,
                label: state.label,
                placeholder: state.placeholder,
                required: state.required,
              }}
            />
          )}
        </Grid.Col>
        <Grid.Col xs={12} sm={6}>
          {zip?.enabled && (
            <DefaultTextField
              {...{
                name: 'zip',
                onChange: handleChange('zip'),
                value: value['zip'] ?? '',
                helperText: zip.errorMessage,
                label: zip.label,
                placeholder: zip.placeholder,
                required: zip.required,
              }}
            />
          )}
        </Grid.Col>
        <Grid.Col xs={12} sm={6}>
          {country?.enabled && (
            <DefaultSelectField
              {...{
                name: 'country',
                onChange: handleChange('country'),
                value: value['country'] ?? '',
                helperText: country.errorMessage,
                label: country.label,
                placeholder: country.placeholder,
                required: country.required,
                options: country.options,
              }}
            />
          )}
        </Grid.Col>
      </Grid>

      <FieldErrors errors={errors} />
      {/* </Stack> */}
    </FormControl>
  );
});
